<template>
  <div class="page-stack">
    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Dictionaries</div>
          <h3 class="section-title">变量字典模板</h3>
          <p class="section-subtitle">维护 Section、基础价格、默认库存、Variation 名称以及不同组合对应的价格。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <input v-model.trim="keyword" class="field-input min-w-[220px]" placeholder="搜索模板名称或 Section">
          <button class="btn btn-primary" @click="openCreateModal">新建字典模板</button>
          <button class="btn btn-secondary" @click="loadTemplates">刷新列表</button>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Templates</div>
          <h3 class="section-title">模板列表</h3>
        </div>
        <div class="text-sm text-slate-400">共 {{ pagination.total }} 条</div>
      </div>
      <div class="table-shell mt-6 overflow-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th>模板</th>
              <th>基础信息</th>
              <th>组合</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in templates" :key="item.id" class="cursor-pointer" @click="openEditModal(item.id)">
              <td class="min-w-[220px]">
                <div class="font-semibold text-slate-900">{{ item.name }}</div>
                <div class="mt-2 text-sm text-slate-500">Section：{{ item.sectionName || '-' }}</div>
              </td>
              <td class="min-w-[220px] text-sm text-slate-600">
                <div>基础价格：{{ item.basePrice }}</div>
                <div class="mt-2">默认库存：{{ item.defaultQuantity }}</div>
                <div class="mt-2">Variation 1：{{ item.variation1Name || '-' }}</div>
                <div class="mt-2">Variation 2：{{ item.variation2Name || '-' }}</div>
              </td>
              <td class="min-w-[320px] text-sm text-slate-600">
                <div v-if="item.items?.length" class="space-y-2">
                  <div v-for="combo in item.items" :key="combo.id || `${combo.option1Value}-${combo.option2Value}`">
                    {{ combo.option1Value || '-' }} / {{ combo.option2Value || '-' }} / {{ combo.varPrice }}
                  </div>
                </div>
                <div v-else>-</div>
              </td>
              <td class="text-sm text-slate-500">{{ item.createdAt || '-' }}</td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="btn btn-secondary" @click.stop="openEditModal(item.id)">编辑</button>
                  <button class="btn btn-secondary" @click.stop="removeTemplate(item.id)">删除</button>
                </div>
              </td>
            </tr>
            <tr v-if="!templates.length">
              <td colspan="5" class="table-empty">当前没有字典模板</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        :page-num="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :total-pages="pagination.totalPages"
        @change-page="handlePageChange"
        @change-size="handlePageSizeChange"
      />
    </section>

    <div v-if="modalVisible" class="modal-backdrop" @click.stop>
      <section class="modal-panel modal-panel--lg" @click.stop>
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h4 class="text-2xl font-bold text-slate-900">{{ form.id ? '编辑变量字典' : '新建变量字典' }}</h4>
            <p class="mt-2 text-sm text-slate-500">不同的 Variation 组合价格可以在这里统一维护，产品页只需要选择模板和组合。</p>
          </div>
          <button class="btn btn-secondary" @click="attemptCloseModal">关闭</button>
        </div>

        <div class="modal-panel__body mt-6 space-y-4">
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="field-label">模板名称</label>
              <input v-model.trim="form.name" class="field-input" placeholder="例如：尾巴颜色 + 长度组合">
            </div>
            <div>
              <label class="field-label">Section</label>
              <input v-model.trim="form.sectionName" class="field-input" placeholder="例如：Tail">
            </div>
            <div>
              <label class="field-label">基础价格</label>
              <input v-model.number="form.basePrice" class="field-input" type="number" step="0.01">
            </div>
            <div>
              <label class="field-label">默认库存</label>
              <input v-model.number="form.defaultQuantity" class="field-input" type="number" step="1">
            </div>
            <div>
              <label class="field-label">Variation 1 名称</label>
              <input v-model.trim="form.variation1Name" class="field-input" maxlength="45" placeholder="例如：Color">
              <div class="settings-hint">最多 45 个字符。</div>
            </div>
            <div>
              <label class="field-label">Variation 2 名称</label>
              <input v-model.trim="form.variation2Name" class="field-input" maxlength="45" placeholder="例如：Length">
              <div class="settings-hint">最多 45 个字符。</div>
            </div>
          </div>

          <div class="rounded-[1.4rem] border border-slate-200 bg-white/80 p-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="field-label">选项值生成器</div>
                <div class="text-sm text-slate-500">输入每个维度的选项值后，系统自动生成笛卡尔积组合，你只需要维护每个组合的价格。</div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button class="btn btn-secondary" type="button" @click="generateCartesianItems">生成组合</button>
                <button class="btn btn-secondary" type="button" @click="fillAllPricesWithBase">全部回填基础价格</button>
              </div>
            </div>
            <div class="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">{{ form.variation1Name || 'Variation 1' }} 选项值</label>
                <textarea
                  v-model.trim="form.option1Source"
                  class="field-textarea"
                  placeholder="每行一个，或用英文逗号分隔，例如：&#10;Black&#10;White&#10;Gray"
                />
              </div>
              <div>
                <label class="field-label">{{ form.variation2Name || 'Variation 2' }} 选项值</label>
                <textarea
                  v-model.trim="form.option2Source"
                  class="field-textarea"
                  placeholder="每行一个，或用英文逗号分隔；如果只有一个维度，这里可以留空"
                />
              </div>
            </div>
            <div class="mt-3 text-sm text-slate-500">每个选项值最多 20 个字符。建议先填写选项值，再点“生成组合”。</div>
          </div>

          <div class="rounded-[1.4rem] border border-slate-200 bg-white/80 p-4">
            <div class="flex items-center justify-between gap-3">
              <div class="field-label">组合价格</div>
              <button class="btn btn-secondary" type="button" @click="addItem">新增组合</button>
            </div>
            <div class="mt-4 space-y-3">
              <div v-for="(item, index) in form.items" :key="index" class="grid gap-3 md:grid-cols-[1fr_1fr_160px_120px]">
                <input v-model.trim="item.option1Value" class="field-input" maxlength="20" :placeholder="form.variation1Name || 'Option 1'">
                <input v-model.trim="item.option2Value" class="field-input" maxlength="20" :placeholder="form.variation2Name || 'Option 2'">
                <input v-model.number="item.varPrice" class="field-input" type="number" step="0.01" placeholder="组合价格">
                <button class="btn btn-secondary" type="button" @click="removeItem(index)">删除</button>
              </div>
              <div v-if="!form.items.length" class="text-sm text-slate-400">当前还没有组合项</div>
            </div>
          </div>
        </div>

        <div class="modal-panel__footer">
          <button class="btn btn-primary" @click="submitTemplate">{{ form.id ? '更新模板' : '保存模板' }}</button>
          <button class="btn btn-secondary" @click="resetEditingForm">清空</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue';
import AppPagination from '@/components/AppPagination.vue';
import { variationTemplateApi } from '@/api';
import { confirmAction, notifyError, notifySuccess } from '@/utils/feedback';

const templates = ref([]);
const keyword = ref('');
const modalVisible = ref(false);
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0
});
const form = reactive({
  id: null,
  name: '',
  sectionName: '',
  basePrice: 0,
  defaultQuantity: 25,
  variation1Name: '',
  variation2Name: '',
  option1Source: '',
  option2Source: '',
  items: []
});
const initialSnapshot = ref('');

function resetForm() {
  form.id = null;
  form.name = '';
  form.sectionName = '';
  form.basePrice = 0;
  form.defaultQuantity = 25;
  form.variation1Name = '';
  form.variation2Name = '';
  form.option1Source = '';
  form.option2Source = '';
  form.items = [];
}

function buildFormSnapshot() {
  return JSON.stringify({
    id: form.id,
    name: form.name,
    sectionName: form.sectionName,
    basePrice: form.basePrice,
    defaultQuantity: form.defaultQuantity,
    variation1Name: form.variation1Name,
    variation2Name: form.variation2Name,
    option1Source: form.option1Source,
    option2Source: form.option2Source,
    items: form.items.map((item) => ({
      option1Value: item.option1Value,
      option2Value: item.option2Value,
      varPrice: item.varPrice
    }))
  });
}

function markClean() {
  initialSnapshot.value = buildFormSnapshot();
}

function isDirty() {
  return buildFormSnapshot() !== initialSnapshot.value;
}

function addItem() {
  form.items.push({
    option1Value: '',
    option2Value: '',
    varPrice: form.basePrice || 0
  });
}

function removeItem(index) {
  form.items.splice(index, 1);
}

function openCreateModal() {
  resetForm();
  addItem();
  modalVisible.value = true;
  markClean();
}

async function openEditModal(id) {
  try {
    const res = await variationTemplateApi.detail(id);
    const item = res.data;
    form.id = item.id;
    form.name = item.name;
    form.sectionName = item.sectionName;
    form.basePrice = item.basePrice;
    form.defaultQuantity = item.defaultQuantity;
    form.variation1Name = item.variation1Name;
    form.variation2Name = item.variation2Name;
    form.items = (item.items || []).map((entry) => ({
      option1Value: entry.option1Value,
      option2Value: entry.option2Value,
      varPrice: entry.varPrice
    }));
    form.option1Source = uniqueOptionValues(form.items, 'option1Value').join('\n');
    form.option2Source = uniqueOptionValues(form.items, 'option2Value').join('\n');
    modalVisible.value = true;
    markClean();
  } catch (error) {
    notifyError(error.message || '加载模板失败');
  }
}

function closeModal() {
  modalVisible.value = false;
  initialSnapshot.value = '';
}

async function attemptCloseModal() {
  if (!isDirty()) {
    closeModal();
    return;
  }
  const confirmed = await confirmAction({
    title: '放弃当前编辑',
    message: '当前变量字典还有未保存内容，确认退出吗？',
    confirmText: '放弃并退出',
    tone: 'danger'
  });
  if (confirmed) {
    closeModal();
  }
}

async function loadTemplates() {
  try {
    const res = await variationTemplateApi.list({
      keyword: keyword.value || undefined,
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize
    });
    templates.value = res.data.records || [];
    pagination.total = res.data.total || 0;
    pagination.totalPages = res.data.totalPages || 0;
    if (!templates.value.length && pagination.pageNum > 1 && pagination.total > 0) {
      pagination.pageNum -= 1;
      await loadTemplates();
    }
  } catch (error) {
    notifyError(error.message || '加载变量模板失败');
  }
}

function parseOptionValues(source) {
  return [...new Set(String(source || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean))];
}

function uniqueOptionValues(items, key) {
  return [...new Set((items || [])
    .map((item) => String(item[key] || '').trim())
    .filter(Boolean))];
}

function comboKey(option1Value, option2Value) {
  return `${option1Value || ''}||${option2Value || ''}`;
}

function generateCartesianItems() {
  if (String(form.variation1Name || '').trim().length > 45 || String(form.variation2Name || '').trim().length > 45) {
    notifyError('Variation 名称不能超过 45 个字符');
    return;
  }
  const option1Values = parseOptionValues(form.option1Source);
  const option2Values = parseOptionValues(form.option2Source);
  if (!option1Values.length) {
    notifyError('请先填写 Variation 1 的选项值');
    return;
  }
  if (option1Values.some((item) => item.length > 20) || option2Values.some((item) => item.length > 20)) {
    notifyError('Variation 下属选项值每条不能超过 20 个字符');
    return;
  }
  const currentPriceMap = new Map(
    form.items.map((item) => [comboKey(item.option1Value, item.option2Value), item.varPrice])
  );
  const nextItems = [];
  if (option2Values.length) {
    option1Values.forEach((option1Value) => {
      option2Values.forEach((option2Value) => {
        nextItems.push({
          option1Value,
          option2Value,
          varPrice: currentPriceMap.has(comboKey(option1Value, option2Value))
            ? currentPriceMap.get(comboKey(option1Value, option2Value))
            : (form.basePrice || 0)
        });
      });
    });
  } else {
    option1Values.forEach((option1Value) => {
      nextItems.push({
        option1Value,
        option2Value: '',
        varPrice: currentPriceMap.has(comboKey(option1Value, ''))
          ? currentPriceMap.get(comboKey(option1Value, ''))
          : (form.basePrice || 0)
      });
    });
  }
  form.items = nextItems;
}

function fillAllPricesWithBase() {
  form.items = form.items.map((item) => ({
    ...item,
    varPrice: form.basePrice || 0
  }));
}

async function submitTemplate() {
  if (!form.name || form.basePrice === null || form.basePrice === undefined) {
    notifyError('请填写完整的模板基础信息');
    return;
  }
  if (!form.items.length) {
    notifyError('请至少维护一个组合价格');
    return;
  }
  if (String(form.variation1Name || '').trim().length > 45 || String(form.variation2Name || '').trim().length > 45) {
    notifyError('Variation 名称不能超过 45 个字符');
    return;
  }
  if (form.items.some((item) => String(item.option1Value || '').trim().length > 20 || String(item.option2Value || '').trim().length > 20)) {
    notifyError('Variation 下属选项值每条不能超过 20 个字符');
    return;
  }
  const payload = {
    name: form.name,
    sectionName: form.sectionName,
    basePrice: form.basePrice,
    defaultQuantity: form.defaultQuantity,
    variation1Name: form.variation1Name,
    variation2Name: form.variation2Name,
    items: form.items
  };
  try {
    const isEdit = Boolean(form.id);
    if (form.id) {
      await variationTemplateApi.update(form.id, payload);
    } else {
      await variationTemplateApi.create(payload);
    }
    closeModal();
    resetForm();
    await loadTemplates();
    notifySuccess(isEdit ? '变量模板已更新' : '变量模板已保存');
  } catch (error) {
    notifyError(error.message || '保存变量模板失败');
  }
}

async function resetEditingForm() {
  if (!isDirty()) {
    resetForm();
    addItem();
    markClean();
    return;
  }
  const confirmed = await confirmAction({
    title: '清空当前编辑',
    message: '当前内容还没有保存，确认清空并重新填写吗？',
    confirmText: '确认清空',
    tone: 'danger'
  });
  if (!confirmed) {
    return;
  }
  resetForm();
  addItem();
  markClean();
}

async function removeTemplate(id) {
  if (!(await confirmAction({
    title: '删除变量模板',
    message: '确认删除这条变量模板吗？产品页之后将不能再复用它。',
    confirmText: '确认删除',
    tone: 'danger'
  }))) {
    return;
  }
  try {
    await variationTemplateApi.remove(id);
    if (templates.value.length === 1 && pagination.pageNum > 1) {
      pagination.pageNum -= 1;
    }
    await loadTemplates();
    notifySuccess('变量模板已删除');
  } catch (error) {
    notifyError(error.message || '删除变量模板失败');
  }
}

function handlePageChange(pageNum) {
  pagination.pageNum = pageNum;
  loadTemplates();
}

function handlePageSizeChange(pageSize) {
  pagination.pageSize = pageSize;
  pagination.pageNum = 1;
  loadTemplates();
}

watch(keyword, () => {
  pagination.pageNum = 1;
  loadTemplates();
});

onMounted(loadTemplates);
</script>
